<template>
  <div :class="appClass" dir="rtl" class="mb-14">
    <UpdateSplash :show="showUpdateSplash" />
    <UpdateNotification :show="showUpdateNotification && !showUpdateSplash" />
    <InstallPrompt 
      :show="showInstallPrompt && !showUpdateSplash" 
      :deferred-prompt="deferredPrompt"
      @prompt-used="handlePromptUsed"
      @dismissed="handleInstallDismissed"
    />
    <router-view v-if="!showUpdateSplash"></router-view>
    <BottomNav v-if="showBottomNav && !showUpdateSplash" style="" />
  </div>
</template>

<script>
// import HelloWorld from './components/HelloWorld'
import('./assets/css/style_sass.scss')
import BottomNav from './components/BottomNav.vue'
import UpdateNotification from './components/UpdateNotification.vue'
import UpdateSplash from './components/UpdateSplash.vue'
import InstallPrompt from './components/InstallPrompt.vue'
import { checkForUpdate, quickCheckForUpdate } from './utils/versionChecker'
// Tailwind loaded via PostCSS from src/assets/css/tailwind.css (main.js)

export default {
  name: 'App',

  // components: {
  //   HelloWorld
  // },
  async created () {
    if (this.$workbox) {
      this.$workbox.addEventListener('waiting', () => {
        this.showUpdateUI = true
      })
    }
    
    // Check for update only once on page load
    const hasUpdate = await quickCheckForUpdate()
    console.log('Quick version check result:', hasUpdate)
    if (hasUpdate) {
      console.log('Showing update splash screen')
      this.showUpdateSplash = true
    } else {
      // If no update on initial check, check once more for notification banner
      checkForUpdate().then((result) => {
        if (result.available && !this.showUpdateSplash) {
          this.showUpdateNotification = true
          console.log('Update available:', result)
        }
      })
    }
    
    // Setup PWA install prompt
    this.setupInstallPrompt()
  },
  
  mounted() {
    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt)
    // Listen for app installed event
    window.addEventListener('appinstalled', this.handleAppInstalled)
    
    // Debug: Check installability
    this.checkInstallability()
    
    // Make test method available globally for debugging
    window.testInstallPrompt = () => this.testInstallPrompt()
    console.log('💡 Tip: You can call window.testInstallPrompt() to manually trigger install prompt')
  },
  
  methods: {
    setupInstallPrompt() {
      // Check if app is already installed
      if (window.matchMedia('(display-mode: standalone)').matches) {
        console.log('App is already installed')
        return
      }
    },

    shouldShowInstallPrompt() {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        return false
      }
      if (localStorage.getItem('installPromptShown')) {
        return false
      }
      if (localStorage.getItem('installPromptDismissed')) {
        localStorage.setItem('installPromptShown', 'true')
        localStorage.removeItem('installPromptDismissed')
        return false
      }
      return true
    },

    markInstallPromptSeen() {
      localStorage.setItem('installPromptShown', 'true')
    },
    
    handleBeforeInstallPrompt(e) {
      if (!this.shouldShowInstallPrompt()) {
        console.log('Install prompt already shown before')
        return
      }

      console.log('✅ beforeinstallprompt event fired', e)
      // Prevent the mini-infobar from appearing
      e.preventDefault()
      // Stash the event so it can be triggered later
      this.deferredPrompt = e
      console.log('✅ DeferredPrompt stored, will show prompt in 3 seconds')
      // Show the install prompt after a delay
      setTimeout(() => {
        if (this.deferredPrompt && !this.showUpdateSplash && this.shouldShowInstallPrompt()) {
          console.log('✅ Showing install prompt now')
          this.showInstallPrompt = true
          this.markInstallPromptSeen()
        } else {
          console.log('❌ Cannot show prompt:', { hasPrompt: !!this.deferredPrompt, showUpdateSplash: this.showUpdateSplash })
        }
      }, 3000) // Show after 3 seconds
    },
    
    // Manual test method (can be called from console)
    testInstallPrompt() {
      if (!this.shouldShowInstallPrompt()) {
        console.log('Test: Install prompt already shown before')
        return
      }

      if (this.deferredPrompt) {
        console.log('Test: Showing install prompt manually')
        this.showInstallPrompt = true
      } else {
        console.log('Test: No deferredPrompt available. Checking installability...')
        this.checkInstallability()
        
        // For testing: Try to show prompt anyway if conditions are met
        if (!window.matchMedia('(display-mode: standalone)').matches && 
            'serviceWorker' in navigator && 
            document.querySelector('link[rel="manifest"]')) {
          console.log('⚠️ Test: Creating mock prompt for testing')
          // Note: This won't actually install, but shows the UI
          this.showInstallPrompt = true
        }
      }
    },
    
    checkInstallability() {
      console.log('Checking PWA installability:')
      console.log('- Service Worker:', 'serviceWorker' in navigator ? '✅' : '❌')
      console.log('- Manifest:', document.querySelector('link[rel="manifest"]') ? '✅' : '❌')
      console.log('- Standalone:', window.matchMedia('(display-mode: standalone)').matches ? '✅ (already installed)' : '✅ (not installed)')
      console.log('- DeferredPrompt:', this.deferredPrompt ? '✅' : '❌')
      console.log('- IsSecureContext:', window.isSecureContext ? '✅' : '❌')
    },
    
    handleAppInstalled() {
      console.log('App was installed')
      this.markInstallPromptSeen()
      this.showInstallPrompt = false
      this.deferredPrompt = null
    },
    
    handlePromptUsed() {
      this.markInstallPromptSeen()
      this.deferredPrompt = null
      this.showInstallPrompt = false
    },
    
    handleInstallDismissed() {
      this.markInstallPromptSeen()
      this.showInstallPrompt = false
    }
  },
  
  beforeDestroy() {
    // Remove event listeners
    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt)
    window.removeEventListener('appinstalled', this.handleAppInstalled)
  },
  
  async accept () {
    this.showUpdateUI = false
    await this.$workbox.messageSW({ type: 'SKIP_WAITING' })
  },

  data: () => ({
    showUpdateNotification: false,
    showUpdateSplash: false,
    showInstallPrompt: false,
    deferredPrompt: null
  }),
  components: { BottomNav, UpdateNotification, UpdateSplash, InstallPrompt },
  computed: {
    appClass() {
      const p = this.$route && this.$route.path ? this.$route.path : ''
      if (p === '/login' || p === '/post-login') return 'login-theme'
      if (p.includes('/chat-detail')) return 'app-theme chat-route'
      return 'app-theme'
    },
    showBottomNav() {
      const path = this.$route && this.$route.path ? this.$route.path : ''
      const name = this.$route && this.$route.name ? this.$route.name : ''
      // hide on login/post-login and all messenger pages
      if (path === '/login' || path === '/post-login') return false
      if (path.includes('/messenger')) return false
      // Hide nav-row on chat detail/conversation page
      if (path.includes('/chat-detail') || name === 'conversation' || name === this.$t('conversation')) return false
      // Hide nav-row on Categories page
      if (name === 'Categories' || path.includes('/create-request/categories')) return false
      if (name === 'Groups' || path.includes('/create-request/groups')) return false
      if (name === 'SubGroups' || path.includes('/create-request/subgroups')) return false
      if (name === 'Brands' || path.includes('/create-request/brands')) return false
      if (name === 'RequestPreDefined' || path.includes('/create-request/predefined')) return false
      if (name === 'RequestDetail' || name === 'request_detail' || path.includes('/create-request/request-detail') || path.includes('/create-request/details')) return false
      if (name === 'RequestSignature' || path.includes('/create-request/request-signature')) return false
      if (name === 'RequestAttachments' || path.includes('/create-request/request-attachments')) return false
      if (name === 'RequestAddress' || path.includes('/create-request/request-address')) return false
      return true
    }
  }
}
</script>
<style>
html,body,#app {
  min-height: 100vh;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* Ensure app wrapper is scrollable */
#app {
  min-height: 100vh;
  height: auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* Make the first child wrapper scrollable in case routes mount a wrapper element */
#app > * {
  min-height: 100vh;
  height: auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 80px; /* space for fixed bottom nav */
}

#app > *.login-theme,
#app > *.chat-route {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  overflow: hidden;
  height: 100vh;
  min-height: 100vh;
}

/* Force router-view container to allow vertical scroll if any parent sets overflow hidden */
router-view, [data-router-view] {
  display: block;
  min-height: 100vh;
  overflow-y: auto;
}

/* Hide bottom nav while work dialogs are open so confirm buttons stay visible */
body.work-dialog-open .bg-glass {
  opacity: 0 !important;
  pointer-events: none !important;
  visibility: hidden !important;
}






/* default app background gray for all pages */
/* .v-application.theme--light.app-theme {
  background: #f5f6f8 !important;
} */

/* keep login pages free to use their own blue gradient */
/* .v-application.login-theme {
  background: transparent !important;
} */
</style>
